import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePrintScale } from '@editor/hooks/usePrintScale'
import { TextAnnotationProps } from '@editor/interfaces/canvas/textAnnotation'
import { DrawingParameterTextAnnotation, DrawingType } from '@editor/interfaces/drawing'
import { CanvasConfigV2, CanvasEventsExtra } from '@editor/interfaces/editor'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools, DEFAULT_STYLES, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { addDrawing, updateDrawingParameters } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { updateInspectionItems } from '@editor/pages/projects/editor/store/inspectionItems'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { convertPrintDimension } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setEditingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'
import { Matrix4 } from 'three'

import { getLastCount, getNewOrder } from '../../utils'

const useMainCanvas = (): CanvasConfigV2 => {
  // Contexts
  const { isAdjustingRef } = useBlueprintEditorContext()

  // Hooks
  const { currentPrintScale } = usePrintScale()
  const { calculateOffsetMatrix } = useProjectionPlane()
  const { getIntersection } = usePointSelection()

  // Store
  const dispatch = useAppDispatch()

  const hiddenElementIds = useSelector((state: RootState) => state.toolUtil.core.hiddenElementIds)
  const editingElementId = useSelector((state: RootState) => state.toolUtil.core.editingElementId)
  const disableCreate = useSelector((state: RootState) => state.toolUtil.core.disableCreate)

  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const drawings = useSelector((state: RootState) => state.blueprint.drawings.document.drawings)
  const renderSkipUuids = useSelector((state: RootState) => state.blueprint.drawings.renderSkipUuids)
  const defaultStyles = useSelector((state: RootState) => state.blueprint.drawings.defaultStyles)
  const isAllowedToModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)

  // States
  const [workingAnnotation, setWorkingAnnotation] = useState<[string, string | null]>(['', null])
  const [isVisibleOnMode, setIsVisibleOnMode] = useState(false)

  // Vars
  const isSelectedTool = useMemo(() => selectedTool === BlueprintEditorTools.TextAnnotation, [selectedTool])

  /**
   * Add a new text annotation on mouse up.
   */
  const handleMouseUp = useCallback(
    (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, { isDragged }: CanvasEventsExtra) => {
      if (isDragged || disableCreate) {
        return
      }

      if (workingAnnotation.every((v) => !!v)) {
        setWorkingAnnotation(['', null])
        return
      }

      const point = getIntersection(ev)
      if (!point) return

      // Don't add a new text annotation if:
      // - The selected tool is not TextAnnotation
      // - There is an editing element (we are already editing)
      if (!isSelectedTool || editingElementId || !point) return

      const matrix = new Matrix4()
      matrix.setPosition(point[0], point[1], point[2])

      const drawing_parameters: DrawingParameterTextAnnotation = {
        uuid: crypto.randomUUID(),
        transformation: matrix.toArray(),
        leadAnchors: [],
        order: getNewOrder(drawings),
        styles: defaultStyles,
        width: 200,
      }

      dispatch(
        addDrawing({
          name: `注釈 ${
            getLastCount(
              drawings.map((p) => p.name),
              '注釈',
            ) + 1
          }`,
          drawing_type: DrawingType.TextAnnotation,
          drawing_parameters,
        }),
      )
      dispatch(setEditingElementId(drawing_parameters.uuid))
    },
    [
      isSelectedTool,
      drawings,
      defaultStyles,
      editingElementId,
      workingAnnotation,
      disableCreate,
      getIntersection,
      dispatch,
    ],
  )

  /**
   * The actual visibility can be a bit too quick causing it to render before
   * the camera is switched over. Delay the visibility change to ensure
   * the camera is ready.
   */
  useEffect(() => {
    const check = mode !== Mode.Editor3D

    // The change to hide is immediate, but the change to show is delayed
    if (!check) {
      setIsVisibleOnMode(check)
      return
    }

    setTimeout(() => {
      setIsVisibleOnMode(check)
    }, 50)
  }, [mode])

  return {
    objects: {
      textAnnotations: [
        ...useMemo(
          () =>
            getDrawingsOfType<DrawingParameterTextAnnotation>(
              drawings,
              DrawingType.TextAnnotation,
              renderSkipUuids,
            ).map<TextAnnotationProps>(
              ({ drawing_parameters: { uuid, transformation, topText, bottomText, leadAnchors, styles } }) => ({
                id: uuid,
                transformation,
                offsetTransformation: calculateOffsetMatrix(drawings.length + 10).toArray(), // Annotations is always on top
                leadAnchors,
                topText,
                bottomText,
                visible: isVisibleOnMode && !hiddenElementIds.includes(uuid),
                editable: isAllowedToModify,
                text: {
                  color: styles?.textColor || DEFAULT_STYLES.textColor,
                  size: styles?.textSize || DEFAULT_STYLES.textSize,
                },
                line: {
                  color: styles?.strokeColor,
                  style: styles?.strokeStyle || DEFAULT_STYLES.strokeStyle,
                  thickness: convertPrintDimension(styles?.strokeWidth || 1, currentPrintScale),
                },
                onAdjustmentStart: () => {
                  isAdjustingRef.current = true
                },
                onUpdate: isAllowedToModify
                  ? (id: string, leadId?: string) => {
                      setWorkingAnnotation([id, leadId || null])
                    }
                  : undefined,
                onUpdated: isAllowedToModify
                  ? ({ id, leadAnchors: payloadLeadAnchors, ...rest }) => {
                      // Not all lead anchors are provided, so we need to merge them.
                      // Look for existing lead anchors,
                      // - If found, update them with the new position.
                      // - If not found, add as new lead anchor.
                      const newLeadAnchors =
                        payloadLeadAnchors?.filter((anchor) => !leadAnchors.some((a) => a.id === anchor.id)) || []

                      // merge existing and new lead anchors
                      const updatedLeadAnchors = [...leadAnchors, ...newLeadAnchors].map((anchor) => {
                        const updatedAnchor = payloadLeadAnchors?.find((a) => a.id === anchor.id)
                        return {
                          ...anchor,
                          point: updatedAnchor ? updatedAnchor.point : anchor.point,
                        }
                      })

                      dispatch(
                        updateDrawingParameters({
                          uuid: id,
                          ...rest,

                          leadAnchors: updatedLeadAnchors,
                        }),
                      )
                      setWorkingAnnotation(['', null])

                      // If the lead anchor is linked to an inspection item, update the inspection item
                      const inspectionItemId = updatedLeadAnchors[0]?.inspectionItemId
                      if (inspectionItemId && rest.topText) {
                        dispatch(
                          updateInspectionItems([
                            {
                              inspection_item_id: inspectionItemId,
                              part_name: rest.topText,
                              isPartNameDefaulted: rest.topText.length > 0,
                            },
                          ]),
                        )
                      }
                    }
                  : undefined,
                onRemoveLead: isAllowedToModify
                  ? (ev: React.MouseEvent<HTMLDivElement>, id: string, leadId?: string) => {
                      dispatch(
                        updateDrawingParameters({
                          uuid: id,
                          leadAnchors: leadAnchors.filter((anchor) => anchor.id !== leadId),
                        }),
                      )
                    }
                  : undefined,
              }),
            ),
          [
            drawings,
            renderSkipUuids,
            hiddenElementIds,
            isVisibleOnMode,
            isAllowedToModify,
            currentPrintScale,
            isAdjustingRef,
            calculateOffsetMatrix,
            dispatch,
          ],
        ),
      ],
    },
    events: {
      onMouseUp: handleMouseUp,
      onCancel: useCallback(() => {
        if (!isSelectedTool) return

        dispatch(setEditingElementId(undefined))
      }, [isSelectedTool, dispatch]),
    },
  }
}

export default useMainCanvas
